/// <reference lib="dom"/>
/** @jsx hh */
/** @jsxFrag ChiFragment */
import { ChiFragment, hh } from "@chi/chi";
import type { ParamDict, ParamDictProps, ParamList } from "@utils/params";
import { Noise, type WbaxOscProps } from "@chi/wbax";
import {
  NonZero,
  type SynthDef,
  SynthOpts,
  SynthPreset,
  type SynthVoiceProps,
} from "@/synth/synth.tsx";
import { HatRatios, MultiOsc, OscTypes } from "@/synth/blocks.tsx";

const ExpDecay = (volume: number, decay: number) => {
  const v = NonZero(volume);
  return [{ set: v, at: 0 }, { exp: 0.001 * v, at: decay }];
};

//#region kick
export const JazzKickParams = {
  "osc/type": { type: "list", value: "sine", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc/frequency": { type: "float", value: 100, min: 40, max: 300, step: 0.1 },
  "hipass/frequency": { type: "int", value: 70, min: 20, max: 500 },
  "hipass/q": { type: "float", value: 0.7, min: 0.1, max: 5, step: 0.1 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.18, min: 0.05, max: 0.6, step: 0.01 },
} satisfies ParamDict;
export type JazzKickOpts = ParamDictProps<typeof JazzKickParams>;

export const JazzKick = (props: Partial<JazzKickOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzKickSynth, props);
  const v = o.volume * (props.velocity ?? 1);
  return (
    <gain
      gain={[
        { set: v, at: 0 },
        { linear: 0.01 * v, at: o.decay },
        { linear: 0, at: o.decay + 0.02 },
      ]}
    >
      <biquad type="highpass" frequency={o["hipass/frequency"]} Q={o["hipass/q"]}>
        <osc
          type={o["osc/type"]}
          frequency={o["osc/frequency"]}
          stop={o.decay + 0.1}
          onEnded={props.onEnded}
        />
      </biquad>
    </gain>
  );
};

export const JazzKickSynth: SynthDef<typeof JazzKickParams> = {
  label: "jazz kick",
  params: JazzKickParams,
  Component: JazzKick,
  duration: (o) => o.decay + 0.1,
};
//#endregion

//#region snare
export const JazzSnareParams = {
  "noiseFilter/frequency": { type: "int", value: 3000, min: 500, max: 8000 },
  "noiseFilter/q": { type: "float", value: 0.5, min: 0.1, max: 5, step: 0.1 },
  "osc/type": { type: "list", value: "triangle", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc/frequency": { type: "float", value: 200, min: 80, max: 500, step: 1 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.3, min: 0.05, max: 1, step: 0.01 },
  attack: { type: "float", value: 0.015, min: 0, max: 0.1, step: 0.001 },
} satisfies ParamDict;
export type JazzSnareOpts = ParamDictProps<typeof JazzSnareParams>;

export const JazzSnare = (props: Partial<JazzSnareOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzSnareSynth, props);
  const v = NonZero(o.volume * (props.velocity ?? 1));
  const duration = o.decay + 0.1;
  return (
    <>
      <gain
        gain={[
          { set: 0.001, at: 0 },
          { linear: v, at: o.attack },
          { exp: 0.01 * v, at: o.decay },
        ]}
      >
        <biquad type="lowpass" frequency={o["noiseFilter/frequency"]} Q={o["noiseFilter/q"]}>
          <Noise type="pink" duration={duration} stop={duration} onEnded={props.onEnded} />
        </biquad>
      </gain>
      <gain
        gain={[
          { set: 0.001, at: 0 },
          { linear: 0.5 * v, at: o.attack * 0.5 },
          { exp: 0.001 * v, at: o.decay * 0.4 },
        ]}
      >
        <osc type={o["osc/type"]} frequency={o["osc/frequency"]} stop={duration} />
      </gain>
    </>
  );
};

export const JazzSnareSynth: SynthDef<typeof JazzSnareParams> = {
  label: "jazz snare",
  params: JazzSnareParams,
  Component: JazzSnare,
  duration: (o) => o.decay + 0.1,
};
//#endregion

//#region cymbals
interface MetalProps extends SynthVoiceProps {
  type: WbaxOscProps["type"];
  baseFreq: number;
  bandpass: number;
  bandpassQ: number;
  highpass: number;
  highpassQ: number;
  stop: number;
  gain: [number, number][];
}
const Metal = (props: MetalProps) => (
  <gain
    gain={props.gain.map(([value, at], i) =>
      i === 0 ? { set: value, at } : { exp: NonZero(value), at }
    )}
  >
    <biquad type="highpass" frequency={props.highpass} Q={props.highpassQ}>
      <biquad type="bandpass" frequency={props.bandpass} Q={props.bandpassQ}>
        <MultiOsc
          type={props.type}
          frequency={props.baseFreq}
          ratios={HatRatios}
          stop={props.stop}
          onEnded={props.onEnded}
        />
      </biquad>
    </biquad>
  </gain>
);

export const JazzRideParams = {
  "oscs/type": { type: "list", value: "square", list: OscTypes } as ParamList<typeof OscTypes>,
  "oscs/baseFreq": { type: "float", value: 220, min: 100, max: 600, step: 1 },
  "bandpass/frequency": { type: "int", value: 8000, min: 2000, max: 22050 },
  "bandpass/q": { type: "float", value: 0.5, min: 0.1, max: 5, step: 0.1 },
  "hipass/frequency": { type: "int", value: 2000, min: 500, max: 8000 },
  "hipass/q": { type: "float", value: 0.7, min: 0.1, max: 5, step: 0.1 },
  volume: { type: "float", value: 0.7, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 1.5, min: 0.3, max: 4, step: 0.1 },
} satisfies ParamDict;
export type JazzRideOpts = ParamDictProps<typeof JazzRideParams>;

export const JazzRide = (props: Partial<JazzRideOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzRideSynth, props);
  const v = o.volume * (props.velocity ?? 1);
  return (
    <Metal
      type={o["oscs/type"]}
      baseFreq={o["oscs/baseFreq"]}
      bandpass={o["bandpass/frequency"]}
      bandpassQ={o["bandpass/q"]}
      highpass={o["hipass/frequency"]}
      highpassQ={o["hipass/q"]}
      stop={o.decay + 0.3}
      gain={[[0.00001 * v, 0], [v, 0.003], [0.6 * v, 0.05 * o.decay], [0.00001 * v, o.decay]]}
      onEnded={props.onEnded}
    />
  );
};

export const JazzRideSynth: SynthDef<typeof JazzRideParams> = {
  label: "jazz ride",
  params: JazzRideParams,
  Component: JazzRide,
  duration: (o) => o.decay + 0.3,
};

export const JazzHiHatParams = {
  "oscs/type": { type: "list", value: "square", list: OscTypes } as ParamList<typeof OscTypes>,
  "oscs/baseFreq": { type: "float", value: 110, min: 60, max: 300, step: 1 },
  "bandpass/frequency": { type: "int", value: 8000, min: 2000, max: 22050 },
  "bandpass/q": { type: "float", value: 1, min: 0.1, max: 10, step: 0.1 },
  "hipass/frequency": { type: "int", value: 3000, min: 500, max: 12000 },
  "hipass/q": { type: "float", value: 0.7, min: 0.1, max: 5, step: 0.1 },
  volume: { type: "float", value: 0.8, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.08, min: 0.02, max: 0.4, step: 0.01 },
} satisfies ParamDict;
export type JazzHiHatOpts = ParamDictProps<typeof JazzHiHatParams>;

export const JazzHiHat = (props: Partial<JazzHiHatOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzHiHatSynth, props);
  const v = o.volume * (props.velocity ?? 1);
  return (
    <Metal
      type={o["oscs/type"]}
      baseFreq={o["oscs/baseFreq"]}
      bandpass={o["bandpass/frequency"]}
      bandpassQ={o["bandpass/q"]}
      highpass={o["hipass/frequency"]}
      highpassQ={o["hipass/q"]}
      stop={o.decay + 0.05}
      gain={[[0.00001 * v, 0], [v, 0.003], [0.00001 * v, o.decay]]}
      onEnded={props.onEnded}
    />
  );
};

export const JazzHiHatSynth: SynthDef<typeof JazzHiHatParams> = {
  label: "jazz hat",
  params: JazzHiHatParams,
  Component: JazzHiHat,
  duration: (o) => o.decay + 0.05,
};
//#endregion

//#region rimshot
export const JazzRimshotParams = {
  "noiseFilter/frequency": { type: "int", value: 2000, min: 500, max: 10000 },
  "noiseFilter/q": { type: "float", value: 0.7, min: 0.1, max: 5, step: 0.1 },
  "osc/type": { type: "list", value: "sine", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc/frequency": { type: "float", value: 1200, min: 400, max: 4000, step: 10 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.04, min: 0.01, max: 0.2, step: 0.005 },
} satisfies ParamDict;
export type JazzRimshotOpts = ParamDictProps<typeof JazzRimshotParams>;

export const JazzRimshot = (props: Partial<JazzRimshotOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzRimshotSynth, props);
  const v = o.volume * (props.velocity ?? 1);
  const duration = o.decay + 0.05;
  return (
    <>
      <gain gain={ExpDecay(v, o.decay)}>
        <biquad type="highpass" frequency={o["noiseFilter/frequency"]} Q={o["noiseFilter/q"]}>
          <Noise type="white" duration={duration} stop={duration} onEnded={props.onEnded} />
        </biquad>
      </gain>
      <gain gain={[{ set: 0.7 * v, at: 0 }, ExpDecay(v, Math.min(0.02, o.decay))[1]]}>
        <osc type={o["osc/type"]} frequency={o["osc/frequency"]} stop={duration} />
      </gain>
    </>
  );
};

export const JazzRimshotSynth: SynthDef<typeof JazzRimshotParams> = {
  label: "jazz rimshot",
  params: JazzRimshotParams,
  Component: JazzRimshot,
  duration: (o) => o.decay + 0.05,
};
//#endregion

//#region brush sweep
export const JazzBrushSweepParams = {
  "filter/frequency": { type: "int", value: 4000, min: 500, max: 10000 },
  "filter/q": { type: "float", value: 1.5, min: 0.1, max: 8, step: 0.1 },
  "filter/ramp": { type: "int", value: 1200, min: 200, max: 8000 },
  volume: { type: "float", value: 0.6, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.6, min: 0.1, max: 2, step: 0.05 },
  attack: { type: "float", value: 0.05, min: 0.005, max: 0.3, step: 0.005 },
} satisfies ParamDict;
export type JazzBrushSweepOpts = ParamDictProps<typeof JazzBrushSweepParams>;

export const JazzBrushSweep = (props: Partial<JazzBrushSweepOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzBrushSweepSynth, props);
  const v = o.volume * (props.velocity ?? 1);
  const duration = o.decay + 0.2;
  return (
    <gain
      gain={[
        { set: 0.001, at: 0 },
        { linear: v, at: o.attack },
        { set: v, at: o.decay * 0.4 },
        { linear: 0.001, at: o.decay },
      ]}
    >
      <biquad
        type="bandpass"
        Q={o["filter/q"]}
        frequency={[
          { set: o["filter/frequency"], at: 0 },
          { linear: o["filter/ramp"], at: o.decay },
        ]}
      >
        <Noise type="pink" duration={duration} stop={duration} onEnded={props.onEnded} />
      </biquad>
    </gain>
  );
};

export const JazzBrushSweepSynth: SynthDef<typeof JazzBrushSweepParams> = {
  label: "brush sweep",
  params: JazzBrushSweepParams,
  Component: JazzBrushSweep,
  duration: (o) => o.decay + 0.2,
};
//#endregion

//#region clave
export const JazzClaveParams = {
  "osc1/frequency": { type: "float", value: 2500, min: 1000, max: 6000, step: 10 },
  "osc2/frequency": { type: "float", value: 1500, min: 500, max: 4000, step: 10 },
  "filter/frequency": { type: "int", value: 2000, min: 500, max: 8000 },
  "filter/q": { type: "float", value: 4, min: 0.5, max: 15, step: 0.5 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.03, min: 0.01, max: 0.15, step: 0.005 },
} satisfies ParamDict;
export type JazzClaveOpts = ParamDictProps<typeof JazzClaveParams>;

export const JazzClave = (props: Partial<JazzClaveOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzClaveSynth, props);
  const duration = o.decay + 0.05;
  return (
    <gain gain={ExpDecay(o.volume * (props.velocity ?? 1), o.decay)}>
      <biquad type="bandpass" frequency={o["filter/frequency"]} Q={o["filter/q"]}>
        <gain gain={0.5}>
          <osc frequency={o["osc1/frequency"]} stop={duration} onEnded={props.onEnded} />
          <osc frequency={o["osc2/frequency"]} stop={duration} />
        </gain>
      </biquad>
    </gain>
  );
};

export const JazzClaveSynth: SynthDef<typeof JazzClaveParams> = {
  label: "clave",
  params: JazzClaveParams,
  Component: JazzClave,
  duration: (o) => o.decay + 0.05,
};
//#endregion

//#region hand drums
interface PitchDropProps extends SynthVoiceProps {
  type: WbaxOscProps["type"];
  frequency: number;
  pitchDrop: number;
  bend: number;
  volume: number;
  decay: number;
}
const PitchDrop = (props: PitchDropProps) => (
  <gain gain={ExpDecay(props.volume * (props.velocity ?? 1), props.decay)}>
    <osc
      type={props.type}
      frequency={[
        { set: props.frequency, at: 0 },
        { exp: Math.max(1, props.frequency * props.pitchDrop), at: props.decay * props.bend },
      ]}
      stop={props.decay + 0.1}
      onEnded={props.onEnded}
    />
  </gain>
);

export const JazzCongaParams = {
  "osc/type": { type: "list", value: "sine", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc/frequency": { type: "float", value: 200, min: 80, max: 600, step: 1 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.15, min: 0.05, max: 0.6, step: 0.01 },
  pitchDrop: { type: "float", value: 0.6, min: 0.1, max: 1, step: 0.01 },
} satisfies ParamDict;
export type JazzCongaOpts = ParamDictProps<typeof JazzCongaParams>;

export const JazzConga = (props: Partial<JazzCongaOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzCongaSynth, props);
  return (
    <PitchDrop
      type={o["osc/type"]}
      frequency={o["osc/frequency"]}
      pitchDrop={o.pitchDrop}
      bend={0.6}
      volume={o.volume}
      decay={o.decay}
      velocity={props.velocity}
      onEnded={props.onEnded}
    />
  );
};

export const JazzCongaSynth: SynthDef<typeof JazzCongaParams> = {
  label: "conga",
  params: JazzCongaParams,
  Component: JazzConga,
  duration: (o) => o.decay + 0.1,
};
export const JazzLowCongaSynth = SynthPreset(JazzCongaSynth, "low conga", {
  "osc/frequency": 160,
  decay: 0.18,
});
export const JazzHighCongaSynth = SynthPreset(JazzCongaSynth, "high conga", {
  "osc/frequency": 260,
  decay: 0.12,
});

export const JazzBongoParams = {
  "osc/type": { type: "list", value: "sine", list: OscTypes } as ParamList<typeof OscTypes>,
  "osc/frequency": { type: "float", value: 500, min: 200, max: 1200, step: 5 },
  volume: { type: "float", value: 1, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.08, min: 0.02, max: 0.3, step: 0.005 },
  pitchDrop: { type: "float", value: 0.7, min: 0.2, max: 1, step: 0.01 },
} satisfies ParamDict;
export type JazzBongoOpts = ParamDictProps<typeof JazzBongoParams>;

export const JazzBongo = (props: Partial<JazzBongoOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzBongoSynth, props);
  return (
    <PitchDrop
      type={o["osc/type"]}
      frequency={o["osc/frequency"]}
      pitchDrop={o.pitchDrop}
      bend={0.4}
      volume={o.volume}
      decay={o.decay}
      velocity={props.velocity}
      onEnded={props.onEnded}
    />
  );
};

export const JazzBongoSynth: SynthDef<typeof JazzBongoParams> = {
  label: "bongo",
  params: JazzBongoParams,
  Component: JazzBongo,
  duration: (o) => o.decay + 0.1,
};
export const JazzLowBongoSynth = SynthPreset(JazzBongoSynth, "low bongo", {
  "osc/frequency": 350,
  decay: 0.1,
});
export const JazzHighBongoSynth = SynthPreset(JazzBongoSynth, "high bongo", {
  "osc/frequency": 650,
  decay: 0.07,
});
//#endregion

//#region shaker
export const JazzShakerParams = {
  "filter/frequency": { type: "int", value: 7000, min: 2000, max: 16000 },
  "filter/q": { type: "float", value: 2, min: 0.5, max: 10, step: 0.1 },
  volume: { type: "float", value: 0.7, min: 0, max: 2, step: 0.01 },
  decay: { type: "float", value: 0.05, min: 0.02, max: 0.3, step: 0.005 },
} satisfies ParamDict;
export type JazzShakerOpts = ParamDictProps<typeof JazzShakerParams>;

export const JazzShaker = (props: Partial<JazzShakerOpts> & SynthVoiceProps) => {
  const o = SynthOpts(JazzShakerSynth, props);
  const duration = o.decay + 0.05;
  return (
    <gain gain={ExpDecay(o.volume * (props.velocity ?? 1), o.decay)}>
      <biquad type="bandpass" frequency={o["filter/frequency"]} Q={o["filter/q"]}>
        <Noise type="white" duration={duration} stop={duration} onEnded={props.onEnded} />
      </biquad>
    </gain>
  );
};

export const JazzShakerSynth: SynthDef<typeof JazzShakerParams> = {
  label: "shaker",
  params: JazzShakerParams,
  Component: JazzShaker,
  duration: (o) => o.decay + 0.05,
};
export const JazzGuiroSynth = SynthPreset(JazzShakerSynth, "guiro", {
  decay: 0.2,
  "filter/frequency": 5000,
  "filter/q": 1,
});
//#endregion

export const DrumsJazz = {
  kick: JazzKickSynth,
  snare: JazzSnareSynth,
  ride: JazzRideSynth,
  hat: JazzHiHatSynth,
  rimshot: JazzRimshotSynth,
  brush: JazzBrushSweepSynth,
  clave: JazzClaveSynth,
  conga: JazzCongaSynth,
  lowConga: JazzLowCongaSynth,
  highConga: JazzHighCongaSynth,
  bongo: JazzBongoSynth,
  lowBongo: JazzLowBongoSynth,
  highBongo: JazzHighBongoSynth,
  shaker: JazzShakerSynth,
  guiro: JazzGuiroSynth,
} satisfies Record<string, SynthDef<any>>;
